import { DateOption, getMonthEnd, LocaleDateVariant } from '@methanesat/ui-components';
import { GROUP_MAIR_BY_YEAR } from '../environmentVariables';
import { Platforms } from '../types';

const sortDateAscending = (date1: Date, date2: Date) => date1.getTime() - date2.getTime();

const stacItemDates = [
    // 2021
    new Date(Date.UTC(2021, 7, 31)), // aug
    new Date(Date.UTC(2021, 6, 31)), // jul

    // 2022
    new Date(Date.UTC(2022, 9, 31)), // oct
    new Date(Date.UTC(2022, 10, 30)), // nov
    // 2023
    new Date(Date.UTC(2023, 5, 30)), // jun
    new Date(Date.UTC(2023, 6, 31)), // jul
    new Date(Date.UTC(2023, 7, 31)), // aug
    new Date(Date.UTC(2023, 8, 30)), // sept
    new Date(Date.UTC(2023, 9, 31)) // oct
].sort(sortDateAscending);
// dates
const stacItemDatesWithTotalEmissions = [
    // 2021
    new Date(Date.UTC(2021, 7, 31)), // aug
    // 2023
    new Date(Date.UTC(2023, 5, 30)), // jun
    new Date(Date.UTC(2023, 6, 31)), // jul
    new Date(Date.UTC(2023, 7, 31)), // aug
    new Date(Date.UTC(2023, 8, 30)), // sept
    new Date(Date.UTC(2023, 9, 31)) // oct
].sort(sortDateAscending);

// Monthly date range
// (months are 0-indexed)
//
// TODO: get dates from STAC, rather than this long list
// https://methanesat.atlassian.net/browse/DP-4286
const monthlyGlobalDateOptions = stacItemDatesWithTotalEmissions;

// MSAT date options
const quarterlyGlobalDateOptions = [
    new Date(Date.UTC(2024, 5, 30)), // end of Q2 (jun)
    new Date(Date.UTC(2024, 8, 30)), // end of Q3 (sep)
    new Date(Date.UTC(2024, 11, 31)), // end of Q4 (dec)
    new Date(Date.UTC(2025, 2, 30)), // end of Q1 (mar)
    new Date(Date.UTC(2025, 5, 30)) // end of Q2 (jun)
].sort(sortDateAscending);

// Yearly date options for MAIR
const mairYearlyDateOptions = [
    new Date(Date.UTC(2023, 11, 31, 23, 59, 59, 999)), // 2023
    ...[], // 2022
    new Date(Date.UTC(2021, 11, 31, 23, 59, 59, 999)) //2021
].sort(sortDateAscending);

const mairDateToGlobalDateOption = (date: Date): DateOption => {
    const monthEnd = getMonthEnd(date);
    return {
        value: monthEnd.getTime()
    };
};

const msatDateToGlobalDateOption = (date: Date): DateOption => {
    const month = date.getUTCMonth() + 1;
    const quarter = Math.floor(month / 3);
    const monthEnd = getMonthEnd(date);
    const value = monthEnd.getTime();
    const label = `Q${quarter} ${date.getUTCFullYear()}`;
    return {
        value,
        label
    };
};

const yearToGlobalDateOption = (date: Date): DateOption => {
    return {
        value: date.getTime()
    };
};

/**
 * Date options for global date picker.
 * Will eventually be driven by actual data available.
 */
export const MAIR_GLOBAL_DATE_OPTIONS = GROUP_MAIR_BY_YEAR
    ? mairYearlyDateOptions.map(yearToGlobalDateOption)
    : monthlyGlobalDateOptions.map(mairDateToGlobalDateOption);
export const MSAT_GLOBAL_DATE_OPTIONS = quarterlyGlobalDateOptions.map(msatDateToGlobalDateOption);

const GLOBAL_DATE_OPTIONS_MAP = {
    [Platforms.MAIR]: MAIR_GLOBAL_DATE_OPTIONS,
    [Platforms.MSAT]: MSAT_GLOBAL_DATE_OPTIONS
};

// Selects newest data by default.
export const MAIR_DEFAULT_GLOBAL_DATE = MAIR_GLOBAL_DATE_OPTIONS[MAIR_GLOBAL_DATE_OPTIONS.length - 1];
// Per Susan, for CERA week 2025, we want the second to newest data to be default.
export const MSAT_DEFAULT_GLOBAL_DATE = MSAT_GLOBAL_DATE_OPTIONS[MSAT_GLOBAL_DATE_OPTIONS.length - 1];

const DEFAULT_GLOBAL_DATE_MAP = {
    [Platforms.MAIR]: MAIR_DEFAULT_GLOBAL_DATE,
    [Platforms.MSAT]: MSAT_DEFAULT_GLOBAL_DATE
};

// Date for Permian target - used for intro drawer demo
export const PERMIAN_DATE = mairDateToGlobalDateOption(new Date(Date.UTC(2021, 7, 31)));

export const getGlobalDateOptions = (platform: Platforms): DateOption[] => GLOBAL_DATE_OPTIONS_MAP[platform];
export const getDefaultGlobalDate = (platform: Platforms): DateOption => DEFAULT_GLOBAL_DATE_MAP[platform];

/** Helper function to retrieve the latest collection date for the specified platform */
export const getLatestCollectionDate = (platform: Platforms) => {
    const dateOptions = getGlobalDateOptions(platform);
    const latest = dateOptions.reduce((prev, curr) => (curr.value > prev.value ? curr : prev));
    return latest;
};

/** Returns the timespan variant used for time selectors depending on the platform  */
export const getTimeSelectorTimespan = (platform: Platforms): LocaleDateVariant => {
    return platform === Platforms.MSAT
        ? LocaleDateVariant.quarterly
        : GROUP_MAIR_BY_YEAR
          ? LocaleDateVariant.year
          : LocaleDateVariant.shortMonthYear;
};
